import React, { useState, useEffect } from 'react';
import { ChevronUp, Instagram, Linkedin, X, Zap, Target, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import '../styles/landing.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [showCapituloModal, setShowCapituloModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [capituloFormData, setCapituloFormData] = useState({
    nome: '',
    email: '',
    celular: ''
  });
  const [isSubmittingCapitulo, setIsSubmittingCapitulo] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    whatsapp: '',
    cidade: '',
    instagram: '',
    historia: '',
    motivo: '',
    barreira: '',
    outraBarreira: '',
    faturamento: '',
    consentimento: false
  });
  const [charCounts, setCharCounts] = useState({ historia: 0, motivo: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['inicio', 'proposito', 'historia', 'mentorias', 'inscricao'];
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatWhatsApp = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleInputChange = (field, value) => {
    if (field === 'whatsapp') {
      value = formatWhatsApp(value);
    }
    if (field === 'historia' && value.length > 500) return;
    if (field === 'motivo' && value.length > 300) return;

    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'historia' || field === 'motivo') {
      setCharCounts(prev => ({ ...prev, [field]: value.length }));
    }
  };

  const handleCapituloSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingCapitulo(true);

    try {
      // Send data to backend
      const response = await axios.post(`${API}/capitulo-lead`, capituloFormData);
      
      if (response.data.success) {
        // Open PDF in new tab
        const pdfUrl = 'https://customer-assets.emergentagent.com/job_supere-barreiras/artifacts/jsw1ifc8_Pare-de-Parar.pdf';
        window.open(pdfUrl, '_blank');
        
        // Reset form and close modal
        setCapituloFormData({ nome: '', email: '', celular: '' });
        setShowCapituloModal(false);
        
        // Show success message
        setShowSuccessMessage(true);
        
        // Scroll to top to see message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Erro ao capturar lead:', error);
      toast.error('Erro ao liberar acesso. Tente novamente.');
    } finally {
      setIsSubmittingCapitulo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consentimento) {
      toast.error('Você precisa autorizar o uso dos dados.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send to backend
      const response = await axios.post(`${API}/inscricao-mentoria`, formData);
      
      if (response.data.success) {
        // Reset form
        setFormData({
          nome: '',
          email: '',
          whatsapp: '',
          cidade: '',
          instagram: '',
          historia: '',
          motivo: '',
          barreira: '',
          outraBarreira: '',
          consentimento: false
        });
        setCharCounts({ historia: 0, motivo: 0 });
        
        // Show success message
        setShowSuccessMessage(true);
        
        // Scroll to top to see message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Erro ao enviar inscrição:', error);
      const errorMessage = error.response?.data?.detail || 'Erro ao enviar inscrição. Tente novamente.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-container">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_6d936da6-514a-4f6b-be45-85d2d3d15e00/artifacts/dqttnx17_1AD12704-422C-49DD-844D-D4693B3E5D5A.png" 
              alt="Pare de Parar Logo" 
              className="logo" 
            />
          </div>
          <ul className="navbar-menu">
            <li>
              <button 
                onClick={() => scrollToSection('inicio')} 
                className={activeSection === 'inicio' ? 'active' : ''}
              >
                Início
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('proposito')} 
                className={activeSection === 'proposito' ? 'active' : ''}
              >
                Propósito
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('historia')} 
                className={activeSection === 'historia' ? 'active' : ''}
              >
                História
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('mentorias')} 
                className={activeSection === 'mentorias' ? 'active' : ''}
              >
                Mentorias
              </button>
            </li>
            <li>
              <button 
                onClick={() => scrollToSection('inscricao')} 
                className={activeSection === 'inscricao' ? 'active' : ''}
              >
                Inscrição
              </button>
            </li>
          </ul>
          <button className="theme-toggle" aria-label="Tema">
            ⚡
          </button>
        </div>
      </nav>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="success-message-banner">
          <div className="success-message-content">
            <button 
              className="success-close" 
              onClick={() => setShowSuccessMessage(false)}
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
            <div className="success-icon">🎉</div>
            <h3>Inscrição recebida com sucesso!</h3>
            <p>
              Você receberá um e-mail/WhatsApp se for selecionado(a). <br />
              Enquanto isso, siga-me no Instagram para mais conteúdo sobre empreendedorismo:
            </p>
            <a 
              href="https://www.instagram.com/alvaroalmeida/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="instagram-link"
            >
              <Instagram size={20} />
              @alvaroalmeida
            </a>
          </div>
        </div>
      )}

      {/* Livro Section */}
      <section className="livro-section">
        <div className="section-container">
          <div className="livro-content">
            <p className="livro-text">
              Você deve ter chegado aqui através do livro <strong>NADA FOI EM VÃO</strong>, que escrevi em coautoria com o Joel Jota. Caso você não tenha lido o livro, 
              <button 
                className="livro-link"
                onClick={() => setShowCapituloModal(true)}
              >
                acesse aqui gratuitamente o capítulo "Pare de Parar"
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section id="inicio" className="hero-section">
        <div className="hero-bg">
          <img 
            src="https://images.unsplash.com/photo-1592530392525-9d8469678dac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" 
            alt="Força e Determinação" 
          />
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-pattern"></div>
        <div className="hero-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_6d936da6-514a-4f6b-be45-85d2d3d15e00/artifacts/dqttnx17_1AD12704-422C-49DD-844D-D4693B3E5D5A.png" 
            alt="Pare de Parar" 
            className="hero-logo"
          />
          <h1 className="hero-headline">
            <span>Pare de Parar.</span><br />
            O sucesso está logo depois da barreira.
          </h1>
          <p className="hero-subheadline">
            Mentorias para empreendedores — foco em atravessar barreiras e seguir até o resultado.
          </p>
          <div className="hero-ctas">
            <Button 
              size="lg" 
              className="btn-primary"
              onClick={() => scrollToSection('inscricao')}
            >
              Quero participar das mentorias
            </Button>
            <Button 
              size="lg" 
              className="btn-secondary"
              onClick={() => scrollToSection('historia')}
            >
              Conheça a minha história
            </Button>
          </div>
        </div>
        <button 
          className="scroll-indicator" 
          onClick={() => scrollToSection('proposito')}
          aria-label="Rolar para baixo"
        >
          <ChevronUp className="rotate-180" size={24} />
        </button>
      </section>

      {/* Propósito Section */}
      <section id="proposito" className="proposito-section">
        <div className="section-container">
          <h2 className="section-title gradient">Por que o movimento existe?</h2>
          <p className="section-subtitle">
            Quantos sonhos morrem porque alguém parou cedo demais? O Pare de Parar existe para mudar isso. É um espaço de força, direção e clareza para você <strong>continuar</strong>, mesmo quando tudo parece dizer o contrário.
          </p>
          <div className="proposito-quote">
            Continuar é a decisão que separa quem chega de quem desiste.
          </div>
          <div className="icon-grid">
            <div className="icon-item">
              <div className="icon-circle">
                <TrendingUp size={48} strokeWidth={2.5} />
              </div>
              <h3>Movimento Ascendente</h3>
              <p>Cada passo, por menor que seja, é progresso. Não pare de subir.</p>
            </div>
            <div className="icon-item">
              <div className="icon-circle">
                <Target size={48} strokeWidth={2.5} />
              </div>
              <h3>Clareza e Direção</h3>
              <p>Saber para onde ir e como chegar. Foco no que realmente importa.</p>
            </div>
            <div className="icon-item">
              <div className="icon-circle">
                <Zap size={48} strokeWidth={2.5} />
              </div>
              <h3>Força Interior</h3>
              <p>A energia para continuar vem de dentro. Desperte seu potencial.</p>
            </div>
          </div>
        </div>
      </section>

      {/* História Section */}
      <section id="historia" className="historia-section">
        <div className="section-container">
          <h2 className="section-title gradient">A minha história é a prova.</h2>
          <div className="historia-content">
            <div className="historia-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_supere-barreiras/artifacts/vcf6co3a_IMG_5747.jpeg" 
                alt="Alvaro Almeida e Danielly Almeida" 
                className="alvaro-photo"
              />
              <p className="photo-caption">Alvaro Almeida e Danielly Almeida</p>
            </div>
            <div className="historia-text">
              <p>
                Eu já estive endividado e sem esperança. Fundamos a BCMED num quartinho improvisado e, com a decisão de <strong>não parar</strong>, ela se tornou a maior empresa de equipamentos de estética do Brasil.
              </p>
              <p>
                O que fez a diferença não foi dinheiro, contatos ou sorte. Foi <strong>continuar</strong>. Cada barreira que poderia ter me feito desistir se tornou um degrau para o próximo nível.
              </p>
              <Button 
                size="lg" 
                className="btn-primary"
                onClick={() => setShowModal(true)}
              >
                Ler história completa
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mentorias Section */}
      <section id="mentorias" className="mentorias-section">
        <div className="section-container">
          <h2 className="section-title">Mentorias para empresários que realmente querem Crescer (e não Parar mais)</h2>
          <p className="mentorias-intro">
            A cada ciclo, seleciono empreendedores inscritos para mentorias, com experiências reais, ferramentas e estratégias práticas para atravessar barreiras.
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <span className="feature-icon">🎯</span>
              <h3>Clareza estratégica</h3>
              <p>Definir o caminho certo e saber exatamente onde focar sua energia para resultados reais e duradouros.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">📊</span>
              <h3>Tração comercial</h3>
              <p>Estratégias práticas para conquistar clientes, gerar receita consistente e escalar seu negócio.</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">💪</span>
              <h3>Apoio real de quem viveu na pele</h3>
              <p>Experiências e lições de alguém que passou pelas mesmas dificuldades e chegou ao topo.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Formulário Section */}
      <section id="inscricao" className="inscricao-section">
        <div className="section-container">
          <h2 className="section-title">Inscreva-se para participar</h2>
          <form onSubmit={handleSubmit} className="inscricao-form">
            <div className="form-grid">
              <div className="form-group">
                <Label htmlFor="nome">Nome completo *</Label>
                <Input
                  id="nome"
                  type="text"
                  placeholder="Seu nome e sobrenome"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email">E-mail *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="whatsapp">WhatsApp *</Label>
                <Input
                  id="whatsapp"
                  type="tel"
                  placeholder="(xx) xxxxx-xxxx"
                  value={formData.whatsapp}
                  onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="cidade">Cidade/Estado</Label>
                <Input
                  id="cidade"
                  type="text"
                  placeholder="Ex: São Paulo/SP"
                  value={formData.cidade}
                  onChange={(e) => handleInputChange('cidade', e.target.value)}
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="instagram">Instagram ou LinkedIn (opcional)</Label>
                <Input
                  id="instagram"
                  type="text"
                  placeholder="@seu_perfil ou linkedin.com/in/seu-perfil"
                  value={formData.instagram}
                  onChange={(e) => handleInputChange('instagram', e.target.value)}
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="historia">
                  Conte brevemente sua história como empreendedor(a) *
                  <span className="char-counter">{charCounts.historia}/500</span>
                </Label>
                <Textarea
                  id="historia"
                  placeholder="Conte em até 500 caracteres onde você está e para onde quer ir…"
                  value={formData.historia}
                  onChange={(e) => handleInputChange('historia', e.target.value)}
                  rows={4}
                  required
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="motivo">
                  Por que você deveria participar das mentorias? *
                  <span className="char-counter">{charCounts.motivo}/300</span>
                </Label>
                <Textarea
                  id="motivo"
                  placeholder="O que me faria escolher você para esta mentoria?"
                  value={formData.motivo}
                  onChange={(e) => handleInputChange('motivo', e.target.value)}
                  rows={3}
                  required
                />
              </div>

              <div className="form-group full-width">
                <Label>Qual é sua principal barreira hoje? *</Label>
                <div className="radio-group">
                  {[
                    'Falta de clientes',
                    'Falta de clareza',
                    'Medo de arriscar',
                    'Falta de capital',
                    'Falta de disciplina/constância',
                    'Outro'
                  ].map((option) => (
                    <div key={option} className="radio-item">
                      <input
                        type="radio"
                        id={`barreira-${option}`}
                        name="barreira"
                        value={option}
                        checked={formData.barreira === option}
                        onChange={(e) => handleInputChange('barreira', e.target.value)}
                        required
                      />
                      <Label htmlFor={`barreira-${option}`} className="radio-label">{option}</Label>
                    </div>
                  ))}
                </div>
                {formData.barreira === 'Outro' && (
                  <Input
                    type="text"
                    placeholder="Descreva sua barreira"
                    value={formData.outraBarreira}
                    onChange={(e) => handleInputChange('outraBarreira', e.target.value)}
                    className="mt-2"
                    required
                  />
                )}
              </div>

              <div className="form-group full-width">
                <div className="checkbox-group">
                  <Checkbox
                    id="consentimento"
                    checked={formData.consentimento}
                    onCheckedChange={(checked) => handleInputChange('consentimento', checked)}
                  />
                  <Label htmlFor="consentimento" className="checkbox-label">
                    Autorizo o uso destes dados para seleção e contato. *
                  </Label>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              size="lg" 
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Quero me candidatar'}
            </Button>
          </form>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className="manifesto-section">
        <div className="section-container">
          <h2 className="manifesto-title">Desafios não são barreiras. São degraus.</h2>
          <p className="manifesto-subtitle">Não pare agora. O próximo passo pode mudar tudo.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_6d936da6-514a-4f6b-be45-85d2d3d15e00/artifacts/dqttnx17_1AD12704-422C-49DD-844D-D4693B3E5D5A.png" 
              alt="Pare de Parar" 
            />
          </div>
          <div className="footer-social">
            <a href="https://www.instagram.com/alvaroalmeida/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram size={24} />
            </a>
            <a href="https://www.linkedin.com/in/alvarobcmed/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <Linkedin size={24} />
            </a>
          </div>
          <p className="footer-text">© 2025 Pare de Parar. Todos os direitos reservados.</p>
        </div>
      </footer>

      {/* Modal Capítulo */}
      {showCapituloModal && (
        <div className="modal-overlay" onClick={() => setShowCapituloModal(false)}>
          <div className="modal-content modal-capitulo" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowCapituloModal(false)} aria-label="Fechar">
              <X size={24} />
            </button>
            <div className="modal-body">
              <h2>Acesse o Capítulo "Pare de Parar"</h2>
              <p style={{ fontSize: '1.1rem', marginBottom: '2rem', color: 'var(--color-text-light)' }}>
                Preencha os dados abaixo para ter acesso gratuito ao capítulo do livro <strong>NADA FOI EM VÃO</strong>.
              </p>
              
              <form onSubmit={handleCapituloSubmit}>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="form-group">
                    <Label htmlFor="capitulo-nome">Nome completo *</Label>
                    <Input
                      id="capitulo-nome"
                      type="text"
                      placeholder="Seu nome completo"
                      value={capituloFormData.nome}
                      onChange={(e) => setCapituloFormData(prev => ({ ...prev, nome: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="capitulo-email">E-mail *</Label>
                    <Input
                      id="capitulo-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={capituloFormData.email}
                      onChange={(e) => setCapituloFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="capitulo-celular">Celular (WhatsApp) *</Label>
                    <Input
                      id="capitulo-celular"
                      type="tel"
                      placeholder="(xx) xxxxx-xxxx"
                      value={capituloFormData.celular}
                      onChange={(e) => setCapituloFormData(prev => ({ ...prev, celular: formatWhatsApp(e.target.value) }))}
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="btn-submit"
                  disabled={isSubmittingCapitulo}
                >
                  {isSubmittingCapitulo ? 'Liberando acesso...' : 'Acessar capítulo gratuitamente'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal História */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)} aria-label="Fechar">
              <X size={24} />
            </button>
            <div className="modal-body">
              <h2>A História Completa de Alvaro Almeida</h2>
              
              <h3>Origem e primeiros passos</h3>
              <p>
                Nasci em Foz do Iguaçu. Desde cedo aprendi que nada seria fácil, mas também que nada seria impossível se eu não parasse. Aos 13 anos, criei meu primeiro site — uma aposta ousada numa época em que poucos acreditavam na internet. O projeto ganhou destaque na Revista Veja, e pela primeira vez senti o gosto de criar algo do zero e ver reconhecimento.
              </p>
              <p>
                Mas o sucesso inicial não durou. Abandonei o projeto cedo demais, distraído por outras oportunidades que pareciam mais fáceis. Aprendi da pior forma que <strong>parar cedo é perder a chance de colher</strong>.
              </p>

              <h3>A Falência e o Recomeço</h3>
              <p>
                Anos depois, já adulto e tentando empreender na área de estética, enfrentei minha maior crise. Dívidas acumuladas, sem perspectiva, sem saída aparente. Eu e minha esposa Dani caminhávamos pela cidade tentando encontrar forças para o próximo dia. Foi nessas caminhadas que tomamos a decisão mais importante das nossas vidas: <strong>não pararíamos</strong>.
              </p>
              <p>
                Não foi uma decisão emocional ou motivacional. Foi espiritual e prática. Decidimos confiar, trabalhar com excelência e persistir, mesmo quando tudo dizia para desistir.
              </p>

              <h3>BCMED: Do Quartinho à Liderança Nacional</h3>
              <p>
                Fundamos a BCMED em um quartinho improvisado. Sem investidores, sem capital, sem rede de contatos poderosa. O que tínhamos era foco, estratégia e uma decisão inabalável de <strong>continuar</strong>.
              </p>
              <p>
                Hoje, a BCMED é a maior empresa de equipamentos de estética do Brasil. Não por sorte, não por facilidades — mas porque recusamos parar nas barreiras. Cada "não", cada dificuldade, cada crise foi um degrau.
              </p>

              <h3>A Decisão que Muda Tudo</h3>
              <p>
                Olhando para trás, percebo que o diferencial nunca foi talento excepcional ou condições privilegiadas. Foi a simples e poderosa decisão de <strong>não parar</strong>. Enquanto outros desistiram na primeira, segunda ou terceira barreira, eu continuei. E o sucesso veio.
              </p>
              <p>
                Hoje, meu propósito é compartilhar essa decisão com outros empreendedores. Mostrar que <strong>o sucesso está logo depois da barreira</strong> — e que nada foi em vão.
              </p>

              <div className="modal-cta">
                <Button size="lg" className="btn-primary" onClick={() => {
                  setShowModal(false);
                  scrollToSection('inscricao');
                }}>
                  Quero participar das mentorias
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;